import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeLog } from '@/utils/safeLogger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const redirectTo = searchParams.get('redirect') || '/settings';
  const isDialogMode = searchParams.get('mode') === 'dialog';
  const qrSessionId = searchParams.get('qr_session');
  const [qrAuthSuccess, setQrAuthSuccess] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      if (isDialogMode) {
        safeLog.info('📱 Dialog mode: Ignoring existing session, showing login form');
        return;
      }
      
      if (user) {
        safeLog.info('[Auth] Session detected, redirecting to:', { redirectTo });
        setIsRedirecting(true);
        navigate(redirectTo, { replace: true });
      }
    };
    
    checkAuthAndRedirect();
  }, [user, navigate, redirectTo, isDialogMode]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        safeLog.info(`[Auth] onAuthStateChange event: ${event} session: ${!!session} isDialogMode: ${isDialogMode}`);
        
        if (event === 'SIGNED_IN' && session && !isDialogMode) {
          const redirectPath = sessionStorage.getItem('auth_redirect') || redirectTo;
          sessionStorage.removeItem('auth_redirect');
          safeLog.info('[Auth] SIGNED_IN event, redirecting to:', { redirectPath });
          setIsRedirecting(true);
          navigate(redirectPath, { replace: true });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, redirectTo, isDialogMode]);

  const handleMicrosoftSignIn = () => {
    const redirectAfterAuth = redirectTo || '/settings';
    sessionStorage.setItem('auth_redirect', redirectAfterAuth);
    
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const popup = window.open(
      '/auth-start-microsoft',
      'Microsoft Login',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );
    
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === 'microsoft-auth-success') {
        safeLog.info('[Auth] Microsoft login successful');
        popup?.close();
        window.removeEventListener('message', handleMessage);
        
        if (event.data.session) {
          safeLog.info('[Auth] Setting session from popup data...');
          await supabase.auth.setSession({
            access_token: event.data.session.access_token,
            refresh_token: event.data.session.refresh_token
          });
          safeLog.info('[Auth] Session set in parent window');
          
          safeLog.info('[Auth] Initiating fallback redirect to:', { redirectTo });
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1000);
        } else {
          safeLog.error('[Auth] No session data in message');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
  };


  // Show success screen for QR authentication
  if (qrAuthSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
            <h2 className="text-2xl font-bold text-center">Authentication Successful!</h2>
            <p className="text-muted-foreground text-center">
              You can now close this window and return to your Outlook Add-In.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading screen during redirect
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 py-16">
        <div className="container px-4 max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Welcome to Email2Contact</CardTitle>
              <CardDescription>
                Sign in with your Microsoft account to access your settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button 
                  type="button" 
                  variant="outline" 
                  size="lg"
                  className="w-full"
                  onClick={handleMicrosoftSignIn}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  Sign in with your Microsoft account to access Email2Contact
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
    </div>
  );
}